"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.reducer = exports.makeInitialState = exports.allDeviceClassKeysSorted = exports.allDeviceClassKeys = exports.getPowerConsumption = exports.gridMeanPower = exports.nonSmartRatio = exports.tickMillis = void 0;
const immer_1 = require("immer");
const lodash_1 = __importDefault(require("lodash"));
const prando_1 = __importDefault(require("prando"));
const ramda_1 = __importDefault(require("ramda"));
const action_1 = require("./interfaces/action");
const util_1 = require("./util");
const historySize = 20;
exports.tickMillis = 500;
exports.nonSmartRatio = 0.8;
const gridYearlyEnergy = 58.1 * 1e9 * 1000; // Wh
exports.gridMeanPower = gridYearlyEnergy / (365 * 24); // W
function getPowerConsumption(tick, devices) {
    let total = 0;
    let totalWithoutSavings = 0;
    const byDeviceClass = {};
    const byDeviceClassWithoutSavings = {};
    for (const deviceClassKey of exports.allDeviceClassKeys) {
        byDeviceClass[deviceClassKey] = 0;
        byDeviceClassWithoutSavings[deviceClassKey] = 0;
    }
    const temp = exports.gridMeanPower * getBasePowerConsumptionFromTick(tick);
    total += temp * exports.nonSmartRatio;
    totalWithoutSavings += temp * exports.nonSmartRatio;
    for (const device of devices) {
        total += device.powerConsumption;
        totalWithoutSavings += device.powerConsumptionWithoutSavings;
        byDeviceClass[device.deviceClassKey] += device.powerConsumption;
        byDeviceClassWithoutSavings[device.deviceClassKey] +=
            device.powerConsumptionWithoutSavings;
    }
    return {
        total,
        totalWithoutSavings,
        byDeviceClass,
        byDeviceClassWithoutSavings,
    };
}
exports.getPowerConsumption = getPowerConsumption;
function getBasePowerConsumptionFromTick(tick) {
    return 1;
}
function getPowerProduction(tick, meanProduction, eventOngoing) {
    return meanProduction * (eventOngoing ? 0.95 : 1);
}
function makeDeviceIndicesById(devices) {
    const deviceIndicesById = {};
    for (const [i, device] of devices.entries()) {
        deviceIndicesById[device.id] = i;
    }
    return deviceIndicesById;
}
exports.allDeviceClassKeys = new Set([
    "dishwasher",
    "fridge",
    "microwave",
    "oven",
    "light",
    "heater",
]);
exports.allDeviceClassKeysSorted = ramda_1.default.sortBy((v) => v, [...exports.allDeviceClassKeys]);
function makeInitialState() {
    const meanProduction = exports.gridMeanPower;
    const toggles = exports.allDeviceClassKeysSorted.map((key) => ({
        key,
        targetSavingRatio: 0,
    }));
    const prando = new prando_1.default();
    const devices = [];
    for (let i = 0; i < 50; i++) {
        const deviceClassKey = prando.nextArrayItem(exports.allDeviceClassKeysSorted);
        const id = prando.nextString(32);
        devices.push({
            id,
            deviceClassKey,
            powerConsumption: 3.14,
            powerConsumptionWithoutSavings: 3.14,
            forAllUsers: false,
        });
    }
    const getPowerConsumptionHacked = (tick) => {
        const powerConsumption = getPowerConsumption(tick, devices);
        powerConsumption.total /= exports.nonSmartRatio;
        return powerConsumption;
    };
    return {
        users: [],
        devices,
        deviceIndicesForAllUsers: [],
        deviceIndicesById: makeDeviceIndicesById(devices),
        meanProduction,
        toggles,
        autoAdjust: false,
        simulation: {
            tick: historySize,
            powerConsumption: getPowerConsumptionHacked(historySize),
            powerProduction: getPowerProduction(historySize, meanProduction, false),
        },
        simulationHistory: lodash_1.default.times(historySize, (i) => ({
            tick: i,
            powerConsumption: getPowerConsumptionHacked(i),
            powerProduction: getPowerProduction(i, meanProduction, false),
        })),
        eventOngoing: false,
    };
}
exports.makeInitialState = makeInitialState;
function makeInitialUser(id) {
    return {
        id,
        seed: new prando_1.default(id).hashCode(id), // HACK
    };
}
const reducer = (_state, action) => (0, immer_1.produce)(_state, (state) => {
    var _a, _b;
    switch (action.type) {
        case action_1.ActionType.AddUser: {
            if (state.users.find((u) => u.id === action.userId)) {
                throw new Error(`user already exists: ${action.userId}`);
            }
            state.users.push(makeInitialUser(action.userId));
            break;
        }
        case action_1.ActionType.RemoveUser: {
            state.users = state.users.filter((u) => u.id !== action.userId);
            break;
        }
        case action_1.ActionType.SetToggle: {
            for (const toggle of state.toggles) {
                if (toggle.key === action.key) {
                    toggle.targetSavingRatio = action.targetSavingRatio;
                    return;
                }
            }
            throw new Error(`toggle does not exist: ${action.key}`);
        }
        case action_1.ActionType.SetAutoAdjust: {
            state.autoAdjust = action.autoAdjust;
            break;
        }
        case action_1.ActionType.StartEvent: {
            state.eventOngoing = true;
            break;
        }
        case action_1.ActionType.EndEvent: {
            state.eventOngoing = false;
            break;
        }
        case action_1.ActionType.TickSimulation: {
            if (!action.devices.every((device) => exports.allDeviceClassKeys.has(device.deviceClassKey))) {
                throw new Error("invalid device class key");
            }
            state.devices = action.devices;
            state.deviceIndicesForAllUsers = state.devices
                .map((d, i) => ({ d, i }))
                .filter(({ d }) => d.forAllUsers)
                .map(({ i }) => i);
            state.deviceIndicesById = makeDeviceIndicesById(action.devices);
            state.meanProduction = lodash_1.default.mean(state.simulationHistory.map((v) => v.powerConsumption.totalWithoutSavings));
            state.simulationHistory.shift();
            state.simulationHistory.push(lodash_1.default.cloneDeep((_a = (0, immer_1.original)(state.simulation)) !== null && _a !== void 0 ? _a : state.simulation));
            state.simulation.tick++;
            state.simulation.powerConsumption = action.powerConsumption;
            state.simulation.powerProduction = getPowerProduction(historySize, state.meanProduction, state.eventOngoing);
            if (state.autoAdjust) {
                const classKeysByPriority = [
                    ["light"],
                    ["heater", "fridge"],
                    ["microwave", "oven"],
                    ["dishwasher"],
                ];
                const remainingClassKeys = new Set(exports.allDeviceClassKeysSorted);
                for (const classKey of classKeysByPriority.flatMap((v) => v)) {
                    remainingClassKeys.delete(classKey);
                }
                if (remainingClassKeys.size) {
                    classKeysByPriority.push(ramda_1.default.sortBy((v) => v, [...remainingClassKeys]));
                }
                const cumulativeConsumptionWithoutSavingsByPriority = [];
                let temp = 0;
                for (const classKeysThisPriority of classKeysByPriority) {
                    const consumptionWithoutSavingsThisPriority = lodash_1.default.sum(classKeysThisPriority.map((k) => state.simulation.powerConsumption.byDeviceClassWithoutSavings[k]));
                    temp += consumptionWithoutSavingsThisPriority;
                    cumulativeConsumptionWithoutSavingsByPriority.push(temp);
                }
                const smartDeviceConsumptionWithoutSavings = lodash_1.default.sum(Object.values(state.simulation.powerConsumption.byDeviceClassWithoutSavings).map((v) => v !== null && v !== void 0 ? v : 0));
                const nonSmartDeviceConsumption = state.simulation.powerConsumption.totalWithoutSavings -
                    smartDeviceConsumptionWithoutSavings;
                let expectedConsumption = nonSmartDeviceConsumption;
                for (const classKeysThisPriority of classKeysByPriority) {
                    const consumptionWithoutSavingsThisPriority = lodash_1.default.sum(classKeysThisPriority.map((k) => state.simulation.powerConsumption.byDeviceClassWithoutSavings[k]));
                    const targetSavingAbsolute = expectedConsumption +
                        consumptionWithoutSavingsThisPriority -
                        state.simulation.powerProduction;
                    let targetSavingRatio = targetSavingAbsolute / consumptionWithoutSavingsThisPriority;
                    targetSavingRatio = Math.max(0, Math.min(1, targetSavingRatio));
                    for (const classKey of classKeysThisPriority) {
                        const toggle = state.toggles.find((t) => t.key === classKey);
                        if (!toggle) {
                            continue;
                        }
                        const oldTargetSavingRatio = toggle.targetSavingRatio;
                        const maxStepSize = 0.03;
                        toggle.targetSavingRatio = Math.max(oldTargetSavingRatio - maxStepSize, Math.min(oldTargetSavingRatio + maxStepSize, targetSavingRatio));
                        const expectedConsumptionThisClass = (1 - toggle.targetSavingRatio) *
                            ((_b = state.simulation.powerConsumption.byDeviceClassWithoutSavings[toggle.key]) !== null && _b !== void 0 ? _b : 0);
                        expectedConsumption += expectedConsumptionThisClass;
                    }
                }
            }
            break;
        }
        default: {
            (0, util_1.unreachable)(action);
        }
    }
});
exports.reducer = reducer;
